/**
 * @typedef {'Draft'|'Submitted'|'Received'} POStatus
 *
 * @typedef {Object} PurchaseOrder
 * @property {string} id
 * @property {string} poNumber
 * @property {string} vendorName
 * @property {string} partNumber
 * @property {string} partDescription
 * @property {number} quantity
 * @property {number} unitCost
 * @property {number} totalCost
 * @property {POStatus} status
 * @property {string} date                       - ISO date string
 * @property {string|null} notes
 * @property {string|null} expectedDeliveryDate  - ISO date string; set for vendor-direct POs
 * @property {string|null} linkedOrderId         - FK → partOrders.id
 * @property {string|null} linkedTicketNumber    - display-only (e.g. "INC10052")
 */

/** @type {PurchaseOrder[]} */
export const purchaseOrders = [
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000001', poNumber: 'PO-6001', vendorName: 'Vantage Technology Supply',
    quantity: 10, totalCost: 4200.00,
    lineItems: [
      { partNumber: 'RAM-32GB-DDR5', partDescription: '32GB DDR5 ECC DIMM', quantity: 10, unitCost: 420.00 },
    ],
    status: 'Submitted', date: '2026-03-20', notes: null,
    linkedOrderId: 'e4f5a6b7-0004-4000-8004-400000000001', linkedTicketNumber: 'REP5001',
  },
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000002', poNumber: 'PO-6002', vendorName: 'Crestline Distribution',
    quantity: 5, totalCost: 4375.00,
    lineItems: [
      { partNumber: 'HBA-16G-FC', partDescription: '16Gbps Fibre Channel HBA', quantity: 5, unitCost: 875.00 },
    ],
    status: 'Received', date: '2026-03-08', notes: null,
    linkedOrderId: 'e4f5a6b7-0004-4000-8004-400000000002', linkedTicketNumber: 'REP5002',
  },
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000003', poNumber: 'PO-6003', vendorName: 'Bridgepoint Components',
    quantity: 15, totalCost: 2925.00,
    lineItems: [
      { partNumber: 'NIC-10G-RJ45', partDescription: '10GbE RJ45 Network Card', quantity: 15, unitCost: 195.00 },
    ],
    status: 'Submitted', date: '2026-03-21', notes: null,
    linkedOrderId: 'e4f5a6b7-0004-4000-8004-400000000003', linkedTicketNumber: 'REP5003',
  },
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000004', poNumber: 'PO-6004', vendorName: 'Vantage Technology Supply',
    quantity: 22, totalCost: 9379.90,
    lineItems: [
      { partNumber: 'SSD-3840-NVMe', partDescription: '3.84TB NVMe U.2 SSD',  quantity: 4,  unitCost: 1250.00 },
      { partNumber: 'SSD-960-NVMe',  partDescription: '960GB NVMe M.2 SSD',   quantity: 10, unitCost: 189.99  },
      { partNumber: 'RAM-32GB-DDR5', partDescription: '32GB DDR5 ECC DIMM',   quantity: 8,  unitCost: 310.00  },
    ],
    status: 'Draft', date: '2026-03-22', notes: 'Priority restock — Q2 server refresh.',
    linkedOrderId: 'e4f5a6b7-0004-4000-8004-400000000004', linkedTicketNumber: 'REP5004',
  },
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000005', poNumber: 'PO-6005', vendorName: 'Pinnacle Technology Group',
    quantity: 12, totalCost: 4170.00,
    lineItems: [
      { partNumber: 'PWR-1100-DC', partDescription: 'DC Power Supply 1100W', quantity: 6, unitCost: 420.00 },
      { partNumber: 'PWR-750-AC',  partDescription: 'AC Power Supply 750W',  quantity: 6, unitCost: 275.00 },
    ],
    status: 'Received', date: '2026-03-01', notes: null,
    linkedOrderId: 'e4f5a6b7-0004-4000-8004-400000000005', linkedTicketNumber: 'REP5005',
  },
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000006', poNumber: 'PO-6006', vendorName: 'Crestline Distribution',
    quantity: 21, totalCost: 4602.00,
    lineItems: [
      { partNumber: 'PWR-7200-AC',    partDescription: 'AC Power Supply 720W',    quantity: 12, unitCost: 285.00 },
      { partNumber: 'PWR-1100-DC',    partDescription: 'DC Power Supply 1100W',   quantity: 3,  unitCost: 310.00 },
      { partNumber: 'FAN-CHASSIS-4U', partDescription: '4U Chassis Cooling Fan',  quantity: 6,  unitCost: 42.00  },
    ],
    status: 'Draft', date: '2026-03-23', notes: 'Bundled power + cooling restock for East Coast Warehouse.',
  },
  {
    id: 'g1a2b3c4-0021-4000-8021-210000000007', poNumber: 'PO-6007', vendorName: 'Crestline Distribution',
    quantity: 1, totalCost: 820.00,
    lineItems: [
      { partNumber: 'HBA-16G-FC', partDescription: '16Gbps Fibre Channel HBA', quantity: 1, unitCost: 820.00 },
    ],
    status: 'Received', date: '2026-03-07', expectedDeliveryDate: '2026-03-15',
    linkedOrderId: 'c1a2b3d4-0001-4000-8001-100000000006', linkedTicketNumber: 'INC10092',
    notes: 'Supplier cost-effective vs. stock. Confirmed via phone — Tony Ferreira.',
  },
];

// ---------------------------------------------------------------------------

/**
 * @typedef {'Pending'|'Paid'} PaymentStatus
 *
 * @typedef {Object} SupplierPayment
 * @property {string} id
 * @property {string} paymentNumber
 * @property {string} vendorName
 * @property {number} amount
 * @property {PaymentStatus} status
 * @property {string} dueDate       - ISO date string
 * @property {string|null} paidDate - ISO date string or null
 * @property {string|null} poId     - FK → purchaseOrders.id
 * @property {string|null} poNumber - display-only
 * @property {string|null} notes
 */

/** @type {SupplierPayment[]} */
export const supplierPayments = [
  { id: 'g2a3b4c5-0022-4000-8022-220000000001', paymentNumber: 'PAY-4001', vendorName: 'Crestline Distribution',      amount: 4375.00, status: 'Paid',    dueDate: '2026-03-15', paidDate: '2026-03-13', poId: 'g1a2b3c4-0021-4000-8021-210000000002', poNumber: 'PO-6002', notes: null },
  { id: 'g2a3b4c5-0022-4000-8022-220000000002', paymentNumber: 'PAY-4002', vendorName: 'Pinnacle Technology Group',       amount: 4170.00, status: 'Paid',    dueDate: '2026-03-10', paidDate: '2026-03-09', poId: 'g1a2b3c4-0021-4000-8021-210000000005', poNumber: 'PO-6005', notes: null },
  { id: 'g2a3b4c5-0022-4000-8022-220000000003', paymentNumber: 'PAY-4003', vendorName: 'Vantage Technology Supply', amount: 4200.00, status: 'Pending', dueDate: '2026-04-03', paidDate: null,         poId: 'g1a2b3c4-0021-4000-8021-210000000001', poNumber: 'PO-6001', notes: null },
  { id: 'g2a3b4c5-0022-4000-8022-220000000004', paymentNumber: 'PAY-4004', vendorName: 'Bridgepoint Components', amount: 2925.00, status: 'Pending', dueDate: '2026-04-05', paidDate: null,         poId: 'g1a2b3c4-0021-4000-8021-210000000003', poNumber: 'PO-6003', notes: null },
  { id: 'g2a3b4c5-0022-4000-8022-220000000005', paymentNumber: 'PAY-4005', vendorName: 'Vantage Technology Supply', amount: 9379.90, status: 'Pending', dueDate: '2026-04-07', paidDate: null,         poId: 'g1a2b3c4-0021-4000-8021-210000000004', poNumber: 'PO-6004', notes: null },
];

// ---------------------------------------------------------------------------

/**
 * @typedef {Object} ShippingCost
 * @property {string} id
 * @property {string} shipmentId
 * @property {string} shipmentNumber
 * @property {string} carrier
 * @property {number} cost
 * @property {string} billedDate     - ISO date string
 * @property {string|null} partOrderId
 * @property {string|null} ticketNumber
 */

/** @type {ShippingCost[]} */
export const shippingCosts = [
  { id: 'g3a4b5c6-0023-4000-8023-230000000001', shipmentId: 'b2c3d4e5-0002-4000-8002-200000000001', shipmentNumber: 'SHP-30009', carrier: 'FedEx', cost: 48.50,  billedDate: '2026-03-14', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000006', ticketNumber: 'INC10092' },
  { id: 'g3a4b5c6-0023-4000-8023-230000000002', shipmentId: 'b2c3d4e5-0002-4000-8002-200000000002', shipmentNumber: 'SHP-30011', carrier: 'UPS',   cost: 35.00,  billedDate: '2026-03-18', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000001', ticketNumber: 'INC10041' },
  { id: 'g3a4b5c6-0023-4000-8023-230000000003', shipmentId: 'b2c3d4e5-0002-4000-8002-200000000003', shipmentNumber: 'SHP-30014', carrier: 'FedEx', cost: 112.75, billedDate: '2026-03-22', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000002', ticketNumber: 'INC10052' },
  { id: 'g3a4b5c6-0023-4000-8023-230000000004', shipmentId: 'b2c3d4e5-0002-4000-8002-200000000004', shipmentNumber: 'SHP-30017', carrier: 'UPS',   cost: 28.00,  billedDate: '2026-03-23', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000005', ticketNumber: 'INC10085' },
  { id: 'g3a4b5c6-0023-4000-8023-230000000005', shipmentId: 'b2c3d4e5-0002-4000-8002-200000000005', shipmentNumber: 'SHP-30019', carrier: 'DHL',   cost: 89.40,  billedDate: '2026-03-21', partOrderId: null,                                   ticketNumber: null        },
  { id: 'g3a4b5c6-0023-4000-8023-230000000006', shipmentId: 'b2c3d4e5-0002-4000-8002-200000000006', shipmentNumber: 'SHP-30021', carrier: 'FedEx', cost: 52.00,  billedDate: '2026-03-23', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000007', ticketNumber: 'INC10104' },
];

// ---------------------------------------------------------------------------

/**
 * @typedef {Object} TicketCost
 * @property {string} id
 * @property {string} partOrderId
 * @property {string} ticketNumber
 * @property {number} partCost
 * @property {number} shippingCost
 * @property {number} laborCost
 * @property {number} totalCost
 */

/** @type {TicketCost[]} */
export const ticketCosts = [
  { id: 'g4a5b6c7-0024-4000-8024-240000000001', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000001', ticketNumber: 'INC10041', partCost: 285.00,  shippingCost: 35.00,  laborCost: 60.00, totalCost: 380.00  },
  { id: 'g4a5b6c7-0024-4000-8024-240000000002', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000002', ticketNumber: 'INC10052', partCost: 195.00,  shippingCost: 112.75, laborCost: 60.00, totalCost: 367.75  },
  { id: 'g4a5b6c7-0024-4000-8024-240000000003', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000005', ticketNumber: 'INC10085', partCost: 42.00,   shippingCost: 28.00,  laborCost: 60.00, totalCost: 130.00  },
  { id: 'g4a5b6c7-0024-4000-8024-240000000004', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000006', ticketNumber: 'INC10092', partCost: 875.00,  shippingCost: 48.50,  laborCost: 60.00, totalCost: 983.50  },
  { id: 'g4a5b6c7-0024-4000-8024-240000000005', partOrderId: 'c1a2b3d4-0001-4000-8001-100000000007', ticketNumber: 'INC10104', partCost: 285.00,  shippingCost: 52.00,  laborCost: 60.00, totalCost: 397.00  },
];
